import { useState } from 'react'

import * as d3 from 'd3'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'

import { getCenteredCoordinates, getTransitionDuration } from 'modules/org_chart/lib/coordinates'
import type { Coordinates } from 'modules/org_chart/models/Coordinates'
import type { Dimensions } from 'modules/org_chart/models/Dimensions'
import type { PointNode } from 'modules/org_chart/models/PointNode'

interface Return {
  value: string
  percentage: number
  scale: (ammount: number) => void
  navigateToNode: (node: PointNode, delay?: number) => void
  navigateToCoordinates: (x: number, y: number, delay?: number, avoidTransition?: boolean) => void
  reset: () => void
  isReady?: boolean
  scaleRange: {
    MIN_ZOOM: number
    MAX_ZOOM: number
  }
}

const MIN_ZOOM = 0.37
const MAX_ZOOM = 1
const INITIAL_TRANSFORM = new d3.ZoomTransform(1, 0, 0)

const useZoom = (
  svgElement: SVGGElement | null,
  size: Dimensions,
  initialCoordinates: Coordinates
): Return => {
  const [zoomState, setZoomState] = useState(INITIAL_TRANSFORM)
  const zoom = useMemo(() => d3.zoom(), [])
  const percentage = Math.floor(((zoomState?.k - MIN_ZOOM) * 75) / (MAX_ZOOM - MIN_ZOOM)) + 25

  const getDistance = (x: number, y: number) => {
    const a = x - zoomState.x
    const b = y - zoomState.y

    return Math.sqrt(a * a + b * b)
  }

  interface ZoomEvent {
    transform: d3.ZoomTransform
  }

  const handleZoomChange = useUnsafeCallback(
    (zoomEvent: ZoomEvent) => {
      if (!svgElement) return

      const { transform } = zoomEvent
      setZoomState(transform)
    },
    [svgElement]
  )

  const scale = (amount: number) => {
    if (!svgElement) return

    const svg = d3.select(svgElement)
    zoom.scaleBy(svg.transition().duration(getTransitionDuration()) as any, amount)
  }

  const navigateToCoordinates = (x: number, y: number, delay = 0, avoidTransition?: boolean) => {
    if (!svgElement) return

    const svg = d3.select(svgElement)
    const [centeredX, centeredY] = getCenteredCoordinates(x, y, size)
    const translation = d3.zoomIdentity.translate(-centeredX, -centeredY).scale(1)
    const distance = getDistance(centeredX, centeredY)
    const transitionDuration = avoidTransition ? 0 : getTransitionDuration(distance)

    zoom.transform(svg.transition().delay(delay).duration(transitionDuration) as any, translation)
  }

  const navigateToNode = (pointNode: PointNode, delay?: number) => {
    const { x, y } = pointNode

    navigateToCoordinates(x, y, delay)
  }

  const reset = () => {
    const [initialX, initialY] = initialCoordinates

    navigateToCoordinates(initialX, initialY)
  }

  useUnsafeEffect(() => {
    if (!svgElement) return

    const svg = d3.select(svgElement)

    zoom.on('zoom', handleZoomChange)
    zoom.scaleExtent([MIN_ZOOM, MAX_ZOOM])

    if (zoom.scaleExtent().toString() === [0, Infinity].toString()) {
      reset()
    }

    svg.call(zoom as any).on('dblclick.zoom', null)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svgElement])

  return {
    value: zoomState?.toString() || '',
    percentage,
    scale,
    navigateToNode,
    navigateToCoordinates,
    reset,
    scaleRange: { MIN_ZOOM, MAX_ZOOM },
    isReady: !!svgElement,
  }
}

export default useZoom
